/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChatOutlined, FmdGoodOutlined, InfoOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import Zoom from 'react-medium-image-zoom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Colors from '../../../../Configs/colors';
import 'react-medium-image-zoom/dist/styles.css';
import Elements from '../../../Elements';
import Images from '../../../../Configs/images';
import { getUserData } from '../../../../Utils/storages';
import { getHistoryDetails } from '../../../../Redux/Models/request';
import ROUTES from '../../../../Configs/routes';
import AlertContext from '../../../../Context/AlertContext';

const headerTooltipMessage = 'You can see the detailed order history here. Please note ongoing orders will have late updated information!';

export default function HistoryDetails({
  classes, selectedRequest, error, message, isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { historyId } = useParams();
  const { openAlert } = useContext(AlertContext);
  const user = getUserData();
  const address = selectedRequest?.address || {};
  const selectedReq = JSON.stringify(selectedRequest);
  const requested = selectedRequest.status === 'Requested';
  const { t } = useTranslation();

  const [openTooltip, setOpenTooltip] = useState(false);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (!historyId) {
      navigate(ROUTES.history());
    } else {
      dispatch(getHistoryDetails(user.id, historyId));
    }
  }, [historyId]);

  const handleToEdit = (step) => {
    if (!isLoading && selectedRequest.id) {
      navigate(`${ROUTES.requestStorage()}?edit_step=${step}&selected_req=${selectedReq}`);
    }
  };

  return (
    <Box className={classes.root}>
      <Elements.Navigator
        title={t('history.title2')}
        secondaryBtn={(
          <Elements.TooltipBase
            message={headerTooltipMessage}
            open={openTooltip}
            onClick={() => setOpenTooltip(true)}
            onClose={() => setOpenTooltip(false)}
            icon={<InfoOutlined className="black" fontSize="small" />}
          />
        )}
      />
      {isLoading && <LinearProgress color="inherit" />}
      <Box py={3}>
        <Container>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="inline-flex" flexDirection="column" alignItems="center">
              <img src={selectedRequest.type === 'Request In' ? Images.OenoIn : Images.OenoOut} alt={selectedRequest.type} width="36" height="36" />
              <div className="weight-700 s-12 mt-1 primary-green">
                {selectedRequest.type}
              </div>
            </Box>
            <Box textAlign="right">
              <Box color="#586479" fontSize={12}>{moment(selectedRequest.created_at).format('DD MMM YYYY, HH.mm')}</Box>
              <Box
                display="flex"
                gap={0.5}
                mt={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <img alt="delivery" width="24" height="24" src={selectedRequest.delivery_type?.icon === 'warehouse' ? Images.IconHome : Images.IconDelivery} />
                <Box fontWeight="700" fontSize={12}>{selectedRequest.delivery_type?.name || 'Regular'}</Box>
              </Box>
            </Box>
          </Box>
        </Container>
        <Divider sx={{ my: 2 }} />
        <Container>
          <Box>
            <Box display="flex">
              <Box flex={3}>
                <div className="s-10 mute">{t('history.schedule')}</div>
                <div className="s-12 weight-700 mt-1">{moment(selectedRequest.pickup_date).format('DD MMM YYYY, HH.mm')}</div>
                <div className="weight-600 primary-green link" onClick={() => handleToEdit(3)} hidden={!requested || selectedRequest.type === 'Request Out'}>{t('common.change')}</div>
              </Box>
              <Box flex={2}>
                <div className="s-10 mute">{t('history.stat')}</div>
                <Box className="s-12 weight-700 mt-1" color="#2391D9">{selectedRequest.status}</Box>
              </Box>
            </Box>
            <Box display="flex" mt={2}>
              <Box flex={3}>
                <div className="s-10 mute">{t('history.arrived')}</div>
                <div className="s-12 weight-700 mt-1">{selectedRequest['arrived-warehouse'] ? moment(selectedRequest['arrived-warehouse']).format('DD MMM YYYY, HH.mm') : '-'}</div>
              </Box>
              <Box flex={2}>
                <div className="s-10 mute">{t('history.paid')}</div>
                <Box className="s-12 weight-700 mt-1" component={selectedRequest.payment_url ? 'a' : Box} href={selectedRequest.payment_url}>{selectedRequest.payment_status}</Box>
              </Box>
            </Box>

            {selectedRequest.status === 'Admin Canceled'
            && (
            <Box mt={1.5} bgcolor="#F5F6F8" borderRadius={1} py={1} px={1.5} display="flex" alignItems="flex-start" gap={1}>
              <ChatOutlined sx={{ fontSize: 14 }} />
              <div className="s-12 weight-700">
                {selectedRequest?.cancel_notes}
              </div>
            </Box>
            )}
          </Box>
        </Container>
        <Divider sx={{ my: 2 }} />

        {!!selectedRequest.driver
        && (
        <>
          <Container>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" gap={1} alignItems="center">
                <img className="circle cover" width="40" height="40" alt="courier" src={selectedRequest.driver?.profile_picture} />
                <Box>
                  <Box fontSize={12} fontWeight="700">{selectedRequest.driver?.name || 'Oeno courier'}</Box>
                  <Box fontSize={10} fontWeight="600" mt="2px" className="mute">{selectedRequest.driver?.id}</Box>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gap="2px" alignItems="flex-end">
                <Box className="mute s-10">{t('history.verified')}</Box>
                <Box>
                  <img alt="oenobank" src={Images.Logo} width="32" height="16" />
                </Box>
              </Box>
            </Box>
          </Container>
          <Divider sx={{ my: 2 }} />
        </>
        )}
        <Container>
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <div className="weight-700">{t('history.pickup')}</div>
              <div className="weight-600 primary-green link" onClick={() => handleToEdit(2)} hidden={!requested || selectedRequest.type === 'Request Out'}>{t('common.change')}</div>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={1.5}>
              <div className="s-12">{t('history.from')}</div>
              <div className="weight-700 s-12">{address.is_default ? 'Main address' : address.label}</div>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={1.5}>
              <div className="s-12">{t('history.name')}</div>
              <div className="weight-700 s-12">{address.name}</div>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={1.5}>
              <div className="s-12">{t('history.phone')}</div>
              <div className="weight-700 s-12">
                +
                {address.phone_number}
              </div>
            </Box>
            <Box mt={1.5} bgcolor="#F5F6F8" borderRadius={1} py={1} px={1.5} display="flex" alignItems="flex-start" gap={1}>
              <FmdGoodOutlined sx={{ fontSize: 14 }} />
              <div className="s-12 weight-700">
                {address.address}
                ,
                {' '}
                {address.city}
                ,
                {' '}
                {address.province}
                ,
                {' '}
                {address.country}
                ,
                {' '}
                {address.postal_code}
              </div>
            </Box>
          </Box>
        </Container>
        <Divider sx={{ my: 2 }} />
        <Container>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <div className="weight-700">{t('history.itemDetails')}</div>
              <div className="weight-600 primary-green link" onClick={() => handleToEdit(1)} hidden={!requested || selectedRequest.type === 'Request Out'}>{t('common.change')}</div>
            </Box>
            {!!selectedRequest.items
            && (
            <Box className="weight-400 s-10" color="#404852">
              {selectedRequest.items?.length || 0}
              {' '}
              {t('common.items')}
            </Box>
            )}
            <Box mt={1.5}>
              <Box className="weight-400 s-10" color="#404852">{t('common.notes')}</Box>
              <div className="weight-700 s-12">{selectedRequest.pickup_notes || '-'}</div>
            </Box>
            {
              selectedRequest.items?.map((item, idx) => (
                <Box mt={1.5} bgcolor="#F5F6F8" borderRadius={1} py={0.5} px={1} key={item.id}>
                  <div className="s-10 weight-700">
                    {t('common.item')}
                    {' '}
                    {idx + 1}
                  </div>
                  <Box mt={1}>
                    <div className="mute s-10">{t('history.wine')}</div>
                    <div className="s-12 weight-600">{item.name}</div>
                  </Box>
                  <Box mt={1}>
                    <div className="mute s-10">{t('history.year')}</div>
                    <div className="s-12 weight-600">{item.year}</div>
                  </Box>
                  <Box mt={1}>
                    <div className="mute s-10">{t('history.volume')}</div>
                    <div className="s-12 weight-600">
                      {item.volume}
                    </div>
                  </Box>
                </Box>
              ))
            }

            {
              selectedRequest.box?.map((box, idx) => (
                <Box mt={1.5} bgcolor="#F5F6F8" borderRadius={1} py={0.5} px={1} key={box.id}>
                  <div className="s-10 weight-700">
                    {t('common.box')}
                    {' '}
                    {idx + 1}
                  </div>
                  <Box mt={1}>
                    <div className="mute s-10">{t('history.boxName')}</div>
                    <div className="s-12 weight-600">{box.name}</div>
                  </Box>
                  <Box mt={1}>
                    <div className="mute s-10">{t('history.boxAmount')}</div>
                    <div className="s-12 weight-600">
                      {box.amount}
                      {' '}
                      {t('common.items')}
                    </div>
                  </Box>
                </Box>
              ))
            }

          </Box>
        </Container>
      </Box>
      <Elements.Footer />
    </Box>
  );
}
